import { Countdown } from "@/components/DemoBits";
import { DeviceFrame, DEVICES as LIVE_DEVICES, useLiveSlot } from "@/components/LiveDevice";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DemoDevice, EmulatorApp, PortalDTO } from "@mm-sales/shared";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowRight,
  BarChart3,
  Bell,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  Gauge,
  Gift,
  Globe,
  Heart,
  Image as ImageIcon,
  KeyRound,
  Lightbulb,
  Mail,
  MapPin,
  Megaphone,
  Monitor,
  MousePointerClick,
  Palette,
  Play,
  Presentation,
  Printer,
  Repeat,
  Search,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Square,
  Star,
  Tablet,
  TrendingUp,
  UserCircle2,
  Users,
  Utensils,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { SwitchFactsLoader } from "./SwitchFactsLoader";
import type { Brand } from "./data";
import { type Role as FloorRole, HardwareFloor } from "./hardwareFloor";
import {
  BrowserChrome,
  DeviceArt,
  MonitorFrame,
  PhoneAdminHome,
  PhoneAdminOrders,
  PhoneAdminReports,
  PhoneConnectBusiness,
  PhoneConnectDiscover,
  PhoneConnectWallet,
  PhoneFrame,
  PhoneHome,
  PhoneLocations,
  PhoneMenu,
  PhoneNotifications,
  PhoneOrderConfirmed,
  PhoneReservations,
  PhoneRewards,
  PosScreen,
  SitePreview,
} from "./mocks";

/* ------------------------------------------------------------ primitives */

export const Blue = "#2563eb";

export const PageTitle = ({
  title,
  sub,
  right,
}: {
  title: string;
  sub: string;
  right?: ReactNode;
}) => (
  <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
    <div>
      <h1 className="text-[26px] font-bold tracking-tight text-[#111318]">{title}</h1>
      <p className="mt-0.5 text-[14px] text-[#5b6472]">{sub}</p>
    </div>
    {right}
  </div>
);

export const Card = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "rounded-2xl border border-[#e6e9f0] bg-white shadow-[0_1px_2px_rgba(16,24,40,.04)]",
      className,
    )}
  >
    {children}
  </div>
);

const ICONS: Record<string, typeof Globe> = {
  ordering: ShoppingCart,
  reservations: CalendarDays,
  menu: Utensils,
  design: Palette,
  location: MapPin,
  seo: Search,
  loyalty: Gift,
  notifications: Bell,
  brand: Palette,
  speed: Gauge,
  mobile: Smartphone,
  contact: Mail,
  gallery: ImageIcon,
  add: ShoppingCart,
  discount: Star,
  split: CheckCircle2,
  kitchen: Utensils,
  print: Printer,
  users: Users,
  globe: Globe,
  heart: Heart,
  chart: BarChart3,
  growth: TrendingUp,
  idea: Lightbulb,
  monitor: Monitor,
  phone: Smartphone,
  megaphone: Megaphone,
  clock: Clock,
  dollar: DollarSign,
  repeat: Repeat,
  calendar: CalendarDays,
  gift: Gift,
  star: Star,
  map: MapPin,
  check: CheckCircle2,
};

const tone = (i: number) =>
  [
    "#e0ecff|#2563eb",
    "#ffe4e6|#e11d48",
    "#fef3c7|#d97706",
    "#dcfce7|#16a34a",
    "#fce7f3|#db2777",
    "#e0f2fe|#0284c7",
    "#ede9fe|#7c3aed",
  ][i % 7]!.split("|") as [string, string];

/** Right-column feature list with tinted icon tiles (Website Features / App Features / Try It Out). */
export const FeatureList = ({
  title,
  sub,
  items,
  onPick,
  picked,
}: {
  title: string;
  sub?: string;
  items: { icon: string; title: string; detail: string; key?: string }[];
  onPick?: (k: string) => void;
  picked?: Set<string>;
}) => (
  <Card className="p-4">
    <div className="text-[15px] font-bold">{title}</div>
    {sub ? (
      <p className="mb-2 mt-0.5 text-[12px] text-[#5b6472]">{sub}</p>
    ) : (
      <div className="mb-2" />
    )}
    <ul className="divide-y divide-[#eef0f4]">
      {items.map((f, i) => {
        const Icon = ICONS[f.icon] ?? Sparkles;
        const [bg, fg] = tone(i);
        const done = picked?.has(f.key ?? f.title);
        const Row = onPick ? "button" : "div";
        return (
          <li key={f.title}>
            <Row
              type={onPick ? "button" : undefined}
              onClick={onPick ? () => onPick(f.key ?? f.title) : undefined}
              className={cn(
                "flex w-full items-center gap-3 py-2.5 text-left",
                onPick && "hover:bg-[#f7f8fb]",
              )}
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{
                  background: done ? "#dcfce7" : bg,
                  color: done ? "#16a34a" : fg,
                }}
              >
                {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-[#111318]">{f.title}</span>
                <span className="block text-[12px] text-[#5b6472]">{f.detail}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-[#c1c7d2]" />
            </Row>
          </li>
        );
      })}
    </ul>
  </Card>
);

const BlueButton = ({
  children,
  to,
  onClick,
  className,
  href,
}: {
  children: ReactNode;
  to?: string;
  onClick?: () => void;
  className?: string;
  href?: string;
}) => {
  const cls = cn(
    "inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563eb] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#1d4ed8]",
    className,
  );
  if (to)
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  if (href)
    return (
      <a href={href} className={cls} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
};

/* ------------------------------------------------------------------ Home */

/** Username / password of the prospect's own demo login, with copy buttons. */
function CredentialRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="min-w-[72px] shrink-0 whitespace-nowrap text-[#5b6472]">{label}</span>
      <span className="min-w-0 flex-1 truncate font-mono text-[13px]" title={value ?? ""}>
        {value || "—"}
      </span>
      {value ? (
        <button
          type="button"
          title="Copy"
          className="text-[#5b6472] hover:text-[#111318]"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? (
            <span className="text-[10px] font-semibold">Copied</span>
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      ) : null}
    </div>
  );
}

export function DemoAccountCard({
  d,
  token,
  className,
}: {
  d: PortalDTO;
  token: string;
  className?: string;
}) {
  const login = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/v1/portal/${token}/login-link`),
    onSuccess: (r) => window.open(r.url, "_blank"),
  });
  if (d.demo.status !== "active") return null;
  return (
    <Card className={cn("p-4", className)}>
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf0ff] text-[#2563eb]">
          <KeyRound className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[14px] font-bold">Your demo account</div>
          <div className="text-[11px] text-[#5b6472]">
            Same login on the web, the admin app and the POS
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        <CredentialRow label="Username" value={d.demo.loginEmail} />
        <CredentialRow label="Password" value={d.demo.loginPassword} />
      </div>
      <DeviceCodes d={d} className="mt-3 border-t border-[#e6e8ec] pt-3" />
      {d.demo.adminUrl ? (
        <BlueButton onClick={() => login.mutate()} className="mt-3 w-full">
          {login.isPending ? "Opening…" : "Open your dashboard"}{" "}
          <ExternalLink className="h-4 w-4" />
        </BlueButton>
      ) : null}
    </Card>
  );
}

const DEVICE_LABEL: Record<DemoDevice["type"], string> = {
  POS: "POS register",
  KDS: "Kitchen display (KDS)",
  CFD: "Customer display (CFD)",
  "Self Ordering Kiosk": "Self-ordering kiosk",
};

/**
 * Pairing codes, one per screen role, plus the employee PIN. Every screen (register,
 * kitchen display, customer display, kiosk) is its own registered device with its own
 * code, so several can run side by side; multi-location demos pick the location first.
 */
export function DeviceCodes({
  d,
  className,
}: {
  d: PortalDTO;
  className?: string;
}) {
  const devices = d.demo.devices ?? [];
  const locations = [...new Set(devices.map((x) => x.location))];
  const [loc, setLoc] = useState<string | null>(null);
  const current = loc && locations.includes(loc) ? loc : locations[0];
  const rows = devices.filter((x) => x.location === current);
  if (!rows.length && !d.demo.posPin) return null;
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#5b6472]">
          Device codes
        </div>
        {locations.length > 1 ? (
          <select
            className="max-w-[55%] rounded-md border border-[#e6e8ec] bg-white px-1.5 py-0.5 text-[11px]"
            value={current}
            onChange={(e) => setLoc(e.target.value)}
          >
            {locations.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="space-y-1.5">
        {rows.map((x) => (
          <CredentialRow key={x.code} label={DEVICE_LABEL[x.type] ?? x.type} value={x.code} />
        ))}
        {d.demo.posPin ? <CredentialRow label="Employee PIN" value={d.demo.posPin} /> : null}
      </div>
      <div className="mt-1.5 text-[11px] text-[#5b6472]">
        Enter the code on the device to pair it; the register then asks for the PIN.
      </div>
    </div>
  );
}

export function PosPinCard({ d }: { d: PortalDTO }) {
  if (!(d.demo.devices?.length || d.demo.posPin)) return null;
  return (
    <Card className="p-4">
      <DeviceCodes d={d} />
    </Card>
  );
}

/**
 * Rep-only controls next to a live device: which app is running, time left, End.
 * Renders nothing for prospects (no rep token → mocks).
 */
function LiveBar({ apps, purpose }: { apps: EmulatorApp[]; purpose: string }) {
  const live = useLiveSlot(purpose);
  if (!live.enabled) return null;
  const cur = live.session ? LIVE_DEVICES.find((x) => x.app === live.session!.app) : null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-[12px]">
      {live.capacity ? (
        <span className="text-[#5b6472]">
          {live.capacity.free}/{live.capacity.total} devices free
        </span>
      ) : null}
      {live.session ? (
        <>
          <span className="rounded-md bg-[#eaf0ff] px-2 py-1 font-semibold text-[#2563eb]">
            {cur?.label ?? live.session.app} on {live.session.slot}
          </span>
          <span className="rounded-md bg-[#f2f4f8] px-2 py-1 font-semibold tabular-nums">
            <Countdown to={live.session.expiresAt} />
          </span>
          <button
            type="button"
            onClick={live.end}
            disabled={live.busy}
            className="inline-flex items-center gap-1 rounded-md bg-[#c8102e] px-2.5 py-1 font-semibold text-white disabled:opacity-60"
          >
            <Square className="h-3 w-3" /> End
          </button>
        </>
      ) : (
        <span className="text-[#5b6472]">
          Live device ready — pick {apps.length > 1 ? "an app" : "the POS"} to start it.
        </span>
      )}
      {live.error ? <span className="w-full text-center text-[#c8102e]">{live.error}</span> : null}
    </div>
  );
}

export function HomeSection({
  b,
  d,
  token,
  base,
}: {
  b: Brand;
  d: PortalDTO;
  token: string;
  base: string;
}) {
  const login = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/v1/portal/${token}/login-link`),
    onSuccess: (r) => window.open(r.url, "_blank"),
  });
  const tiles = [
    {
      to: `${base}/website`,
      icon: Globe,
      title: "Website",
      sub: "See your customized website",
      cta: "Explore Website",
      preview: <ScaledSite b={b} />,
    },
    {
      to: `${base}/hardware`,
      icon: Monitor,
      title: "Hardware",
      sub: "Try our SUNMI devices",
      cta: "Explore Hardware",
      preview: <MiniPos b={b} />,
    },
    {
      to: `${base}/mobile`,
      icon: Smartphone,
      title: "Mobile App",
      sub: "Experience your app",
      cta: "Explore App",
      preview: <MiniPhone b={b} />,
    },
    {
      to: `${base}/presentation`,
      icon: Presentation,
      title: "Presentation",
      sub: "See opportunities & solutions",
      cta: "View Presentation",
      preview: <MiniChart />,
    },
  ];
  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[26px] font-bold tracking-tight">
          Welcome{b.first ? `, ${b.first}` : ""}
        </h1>
        <p className="text-[14px] text-[#5b6472]">Explore your customized demo for {b.name}.</p>
      </div>
      <div className="relative mb-5 overflow-hidden rounded-2xl bg-[#0f172a]">
        {b.hero ? (
          <img src={b.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        <div className="relative flex min-h-[260px] flex-col justify-center px-8 py-10 text-white md:px-12">
          <div className="text-[13px] font-medium uppercase tracking-[0.2em] text-white/80">
            Welcome to your demo
          </div>
          <div className="mt-1 text-[40px] font-extrabold uppercase leading-none tracking-wide md:text-[52px]">
            {b.name}
          </div>
          <div className="mt-2 text-[18px] text-white/90">{b.tagline}</div>
          <p className="mt-3 max-w-2xl text-[14px] text-white/85">
            Try everything. Click around. Experience how MyManager can help you run, grow, and
            simplify your {b.cuisine ? "restaurant" : "business"}.
          </p>
          {d.demo.adminUrl ? (
            <div className="mt-5">
              <BlueButton onClick={() => login.mutate()}>
                {login.isPending ? "Opening…" : "Open your dashboard"}{" "}
                <ExternalLink className="h-4 w-4" />
              </BlueButton>
            </div>
          ) : null}
        </div>
      </div>
      <DemoAccountCard d={d} token={token} className="mb-5" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.to} className="flex flex-col p-4">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#eaf0ff] text-[#2563eb]">
                <t.icon className="h-4.5 w-4.5" />
              </span>
              <div>
                <div className="text-[14px] font-bold">{t.title}</div>
                <div className="text-[11px] text-[#5b6472]">{t.sub}</div>
              </div>
            </div>
            <div className="my-3 h-36 overflow-hidden rounded-xl bg-[#f7f8fb]">{t.preview}</div>
            <BlueButton to={t.to} className="mt-auto w-full">
              {t.cta} <ArrowRight className="h-4 w-4" />
            </BlueButton>
          </Card>
        ))}
      </div>
      <ReadyCta b={b} />
    </div>
  );
}

const ScaledSite = ({ b }: { b: Brand }) => (
  <div className="relative h-full w-full overflow-hidden">
    <div className="pointer-events-none absolute left-0 top-0 w-[1100px] origin-top-left scale-[0.27]">
      <SitePreview b={b} compact />
    </div>
  </div>
);
const MiniPos = ({ b }: { b: Brand }) => (
  <div className="flex h-full items-end justify-center px-4 pb-1">
    <div className="w-full overflow-hidden rounded-t-lg border-[5px] border-b-0 border-[#111318] bg-white">
      <div className="pointer-events-none h-[110px] w-[560px] origin-top-left scale-[0.42]">
        <div className="h-[262px] w-[560px]">
          <PosScreen b={b} />
        </div>
      </div>
    </div>
  </div>
);
const MiniPhone = ({ b }: { b: Brand }) => (
  <div className="flex h-full items-end justify-center">
    <div className="pointer-events-none h-[150px] w-[88px] overflow-hidden rounded-t-[16px] border-[4px] border-b-0 border-[#111318] bg-white">
      <div className="origin-top-left scale-[0.34]">
        <div className="h-[440px] w-[236px]">
          <PhoneHome b={b} />
        </div>
      </div>
    </div>
  </div>
);
const MiniChart = () => (
  <svg viewBox="0 0 200 110" className="h-full w-full p-3" aria-hidden="true">
    {[30, 55, 40, 70, 60, 90].map((h, i) => (
      <rect
        key={h}
        x={14 + i * 30}
        y={100 - h}
        width="18"
        height={h}
        rx="3"
        fill={i === 5 ? "#2563eb" : "#c7d7fe"}
      />
    ))}
    <polyline
      points="23,80 53,60 83,70 113,45 143,50 173,20"
      fill="none"
      stroke="#f59e0b"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const ReadyCta = ({ b }: { b: Brand }) => (
  <Card className="mt-6 grid gap-6 p-6 md:grid-cols-[1.4fr_1fr]">
    <div>
      <h2 className="text-[22px] font-bold">Ready to Make It Yours?</h2>
      <p className="text-[14px] text-[#5b6472]">
        Turn your demo into a live account and start growing your business.
      </p>
      <ul className="mt-4 space-y-3">
        {[
          ["All Your Demo Data Will Be Saved", "Keep your settings and customizations"],
          ["Fast & Easy Setup", "Our team will handle the rest"],
          ["Dedicated Support", "We'll be with you every step of the way"],
        ].map(([t, s]) => (
          <li key={t} className="flex gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#16a34a]" />
            <div>
              <div className="text-[14px] font-semibold">{t}</div>
              <div className="text-[12px] text-[#5b6472]">{s}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
    <div className="flex flex-col items-center justify-center rounded-xl border border-[#e6e9f0] bg-[#f7f8fb] p-6 text-center">
      {b.logo ? (
        <img src={b.logo} alt="" className="h-12 w-12 rounded-full object-cover" />
      ) : (
        <Utensils className="h-10 w-10" style={{ color: b.color }} />
      )}
      <div className="mt-2 text-[15px] font-bold uppercase">{b.name}</div>
      <p className="mt-1 text-[13px] text-[#5b6472]">
        Let's bring your {b.cuisine ? "restaurant" : "business"} online. No obligation. Just the
        next step.
      </p>
      <BlueButton
        href={`mailto:${b.agent.email}?subject=${encodeURIComponent(`${b.name} — let's get started`)}`}
        className="mt-4 w-full"
      >
        Let's get started <ArrowRight className="h-4 w-4" />
      </BlueButton>
    </div>
  </Card>
);

/* --------------------------------------------------------------- Website */

export function WebsiteSection({ b, d }: { b: Brand; d: PortalDTO }) {
  const [mode, setMode] = useState<"desktop" | "tablet" | "mobile">("desktop");
  // "Your site" = the website we built for them (prospect.builtSiteUrl) when the rep
  // has set one, otherwise the branded preview generated from their imported data.
  // "Live storefront" = their real ShopV2 online store inside the demo (the same store
  // the Online Shop section embeds), not the legacy /shop page.
  const [view, setView] = useState<"preview" | "live">("preview");
  const builtSite = d.prospect.builtSiteUrl || null;
  const storeUrl =
    d.demo.onlineShopUrl || d.locations.find((l) => l.onlineShopUrl)?.onlineShopUrl || null;
  // Address bar for the store: the demo org's own branded host when we know it.
  const storeShown = (() => {
    if (!storeUrl) return null;
    try {
      const origin = d.demo.websiteUrl ? new URL(d.demo.websiteUrl).origin : null;
      return origin ? `${origin}/online-shop` : storeUrl;
    } catch {
      return storeUrl;
    }
  })();
  const live = view === "live" && Boolean(storeUrl);
  const width = mode === "desktop" ? "100%" : mode === "tablet" ? "820px" : "400px";
  const url = live ? (storeShown as string) : builtSite || `https://${b.websiteHost}`;
  const openUrl = live ? storeUrl : builtSite;
  const [picked, setPicked] = useState(new Set<string>());
  return (
    <div>
      <PageTitle
        title="Website Simulator"
        sub="This is your live, interactive website. Click around, explore features, and see how your restaurant looks online."
        right={
          <div className="flex items-center gap-2">
            {storeUrl ? (
              <div className="flex rounded-lg border border-[#e6e9f0] bg-white p-1">
                {(
                  [
                    ["preview", "Your site"],
                    ["live", "Live storefront"],
                  ] as const
                ).map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    className={cn(
                      "rounded-md px-3 py-2 text-[13px] font-semibold",
                      view === v ? "bg-[#0f172a] text-white" : "text-[#5b6472] hover:bg-[#f7f8fb]",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="flex rounded-lg border border-[#e6e9f0] bg-white p-1">
              {(
                [
                  ["desktop", Monitor, "Desktop"],
                  ["tablet", Tablet, "Tablet"],
                  ["mobile", Smartphone, "Mobile"],
                ] as const
              ).map(([m, I, l]) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-4 py-2 text-[13px] font-semibold",
                    mode === m ? "bg-[#2563eb] text-white" : "text-[#5b6472] hover:bg-[#f7f8fb]",
                  )}
                >
                  <I className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>
            {openUrl ? (
              <a
                href={openUrl}
                target="_blank"
                rel="noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#e6e9f0] bg-white text-[#5b6472] hover:text-[#2563eb]"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : null}
          </div>
        }
      />
      <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
        <div className="mx-auto w-full transition-all" style={{ maxWidth: width }}>
          <BrowserChrome url={url}>
            <div className="max-h-[78vh] overflow-y-auto">
              {live && storeUrl ? (
                <iframe title="online store" src={storeUrl} className="h-[78vh] w-full border-0" />
              ) : builtSite ? (
                <iframe title="your site" src={builtSite} className="h-[78vh] w-full border-0" />
              ) : (
                <SitePreview b={b} compact={mode === "mobile"} />
              )}
            </div>
          </BrowserChrome>
        </div>
        <div className="space-y-4">
          <FeatureList
            title="Try It Yourself"
            sub="Click around the live website to experience it like your customers will."
            picked={picked}
            onPick={(k) => setPicked((s) => new Set(s).add(k))}
            items={[
              {
                icon: "menu",
                title: "View Menu",
                detail: "See how your menu is displayed",
              },
              {
                icon: "reservations",
                title: "Make a Reservation",
                detail: "Try the online booking flow",
              },
              {
                icon: "ordering",
                title: "Order Online",
                detail: "Experience the ordering process",
              },
              {
                icon: "mobile",
                title: "View on Mobile",
                detail: "See how it looks on a phone",
                key: "mobile",
              },
              {
                icon: "contact",
                title: "Contact Form",
                detail: "Test the contact form",
              },
              {
                icon: "gallery",
                title: "Gallery",
                detail: "Explore your photos and gallery",
              },
              {
                icon: "speed",
                title: "Check Website Speed",
                detail: "See how fast your site loads",
              },
            ]}
          />
          <FeatureList
            title="Website Features"
            items={[
              {
                icon: "ordering",
                title: "Online Ordering",
                detail: "Let customers order directly",
              },
              {
                icon: "reservations",
                title: "Reservations",
                detail: "Accept and manage reservations",
              },
              {
                icon: "menu",
                title: "Menu Management",
                detail: "Easily update your menu",
              },
              {
                icon: "design",
                title: "Branded Design",
                detail: "Your logo, colors, and images",
              },
              {
                icon: "location",
                title: "Contact & Location",
                detail: "Hours, map, and directions",
              },
              {
                icon: "seo",
                title: "SEO Ready",
                detail: "Get found on Google",
              },
            ]}
          />
          <div className="rounded-2xl border border-[#c7d7fe] bg-[#eef3ff] p-4">
            <div className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2563eb] text-white">
                <Lightbulb className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[14px] font-bold text-[#1e3a8a]">Your Actual Website</div>
                <p className="text-[12px] text-[#3b4a6b]">
                  This is your real website with your content, images, and settings. You can make
                  changes any time from your dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Hardware */

const DEVICES = [
  {
    key: "d3",
    name: "SUNMI T3 Pro",
    sub: "POS Terminal",
    art: "pos",
    group: "POS Terminals",
    blurb: "All-in-One POS Terminal",
    specs: [
      '15.6" Touch Display',
      "Fast & Reliable",
      "Built for Restaurants",
      "Integrated Payments",
    ],
  },
  {
    key: "p3h",
    name: "SUNMI P3H",
    sub: "Handheld",
    art: "handheld",
    photo: "/hardware/p3h.png",
    group: "Handheld",
    blurb: "Handheld ordering & payments",
    specs: ['6.75" display', "Built-in printer", "Take orders tableside", "Tap to pay"],
  },
  {
    key: "cpad",
    name: "SUNMI CPad Pay",
    sub: "Handheld",
    art: "handheld",
    photo: "/hardware/cpad-pay.png",
    group: "Handheld",
    blurb: "Ordering and payment on one pad.",
    specs: ["Order and pay together", "Tap, insert, or swipe", "NFC payments", "Counter pad"],
  },
  {
    key: "k2",
    name: "SUNMI Flex 3",
    sub: "Kiosk",
    art: "kiosk",
    group: "Kiosks",
    blurb:
      "They order and pay themselves. The phone is their rewards account, and a text goes out when it's ready.",
    specs: [
      "Name and mobile before they order",
      "Phone finds or creates the contact",
      "Points land when they pay",
      "A reward on the account comes off the check",
      "Text when the order is ready",
      "Tip, then card on the kiosk",
    ],
  },
  {
    key: "kds",
    name: "SUNMI Flex 3",
    sub: "Kitchen display",
    art: "display",
    group: "Kitchen Display",
    blurb: "The kitchen board. Dish counts, modifier counts, and delivery orders.",
    specs: [
      "Counts per item and per modifier",
      "Pending, preparing, and ready",
      "All-day ingredient counts",
      "Cancel Uber, DoorDash, or Grubhub",
      "Uber and DoorDash phone codes",
      "Bump, recall, and prep timers",
    ],
  },
  {
    key: "cfd",
    name: "SUNMI T3 Pro",
    sub: "Customer display",
    art: "display",
    group: "POS Terminals",
    blurb: "Customer-facing display",
    specs: ["Shows the order live", "Tips & signature", "Loyalty sign-up", "Promotions"],
  },
  {
    key: "printer-kitchen",
    name: "Kitchen Cloud Printer",
    sub: "80mm kitchen",
    art: "printer",
    photo: "/hardware/printer-kitchen.png",
    group: "Printers",
    blurb: "A ticket prints the moment the order is sent.",
    specs: [
      "Fires on send to kitchen",
      "Kitchen, bar, and prep stations",
      "Modifiers, variants, and notes",
      "Table, server, and course",
      "In-store and delivery apps",
      "Reprint shows it's a copy",
    ],
  },
  {
    key: "printer-label",
    name: "80mm Label Printer",
    sub: "Labels",
    art: "printer",
    photo: "/hardware/printer-label.png",
    group: "Printers",
    blurb: "One sticker per item, for the bag or the cup.",
    specs: [
      "Order number and item 2 of 4",
      "Name, qty, variant, modifiers",
      "Fixed size or linerless",
      "Packing and expo",
      "Prints with the order",
      "80mm",
    ],
  },
  {
    key: "printer-scan",
    name: "Label Printer + Scanner",
    sub: "80mm print & scan",
    art: "printer",
    photo: "/hardware/printer-label-scan.png",
    group: "Printers",
    blurb: "Prints the item label, and the scanner rings the product in.",
    specs: [
      "Item labels, same as the 80mm",
      "Barcode or SKU adds the item",
      "Scan a customer onto the check",
      "No separate scanner",
      "Packing station",
    ],
  },
  {
    key: "printer-58",
    name: "58mm Label Printer",
    sub: "Labels & receipts",
    art: "printer",
    photo: "/hardware/printer-58.png",
    group: "Printers",
    blurb: "The guest check and the customer receipt, on a small counter printer.",
    specs: [
      "Customer and merchant copies",
      "Tips and a signature line",
      "Logo, order number, items",
      "Cash sale opens the drawer",
      "58mm item labels too",
      "LAN, USB, Bluetooth, or cloud",
    ],
  },
  {
    key: "drawer",
    name: "SUNMI MAX",
    sub: "Cash drawer",
    art: "drawer",
    photo: "/hardware/max-cash-drawer.png",
    group: "Accessories",
    blurb: "Pops open on a cash sale, and the register can see if it is still open.",
    specs: [
      "Opens with the cash sale",
      "5 bill trays and a check slot",
      "4 fixed and 6 removable coin trays",
      "USB, Bluetooth, or the receipt printer",
      "Lock by software, key, or shut",
      "9V, 12V, or 24V",
    ],
  },
  {
    key: "scanner",
    name: "SUNMI Blink 2",
    sub: "Barcode scanner",
    art: "scanner",
    photo: "/hardware/blink-2.png",
    group: "Accessories",
    blurb: "Sits on the counter at 45°. A barcode or QR lands the item on the check.",
    specs: [
      "1D and 2D, paper or a phone",
      "Barcode or SKU adds the item",
      "A customer code attaches them",
      "Type-A or Type-C, plug and play",
      "Light and a beep",
    ],
  },
  {
    key: "scanner-2d",
    name: "SUNMI Scanner",
    sub: "2D handheld",
    art: "scanner",
    photo: "/hardware/scanner-2d.png",
    group: "Accessories",
    blurb: "The handheld gun. Screen or paper, then the item hits the check.",
    specs: [
      "1D and 2D, even a wrinkled code",
      "White light and a red aim",
      "Barcode or SKU adds the item",
      "A customer code attaches them",
      "Buzzer, vibration, and a light",
      "USB, or hands-free on the stand",
    ],
  },
] as const;
// "All Devices" is the floor: every device is live from there (register, handheld,
// kiosk, customer display), so the separate Demo tab was dropped on 2026-09-23.
const GROUPS = [
  "All Devices",
  "POS Terminals",
  "Handheld",
  "Kiosks",
  "Kitchen Display",
  "Printers",
  "Accessories",
];
/** Printers split into the two jobs they do, so the list and the stage read as sets. */
const PRINTER_FAMILIES: { label: string; keys: (typeof DEVICES)[number]["key"][] }[] = [
  { label: "Kitchen", keys: ["printer-kitchen"] },
  { label: "Labels", keys: ["printer-label", "printer-58"] },
];
/** Counter scanner, handheld gun, and the label printer that scans. One set, not three rows. */
const SCANNER_KEYS: (typeof DEVICES)[number]["key"][] = ["scanner", "scanner-2d", "printer-scan"];

/** What actually happens, and the extra facts that don't fit in three steps. */
const DEVICE_STORY: Record<string, { title: string; detail: string }[]> = {
  "printer-kitchen": [
    { title: "Send the order", detail: "From the register, the kiosk, or a delivery app." },
    {
      title: "It picks a station",
      detail: "Kitchen, bar, prep, expo, or packing, based on the item.",
    },
    { title: "The ticket prints", detail: "Modifiers, the table, the server, and the course." },
  ],
  "printer-label": [
    { title: "The order goes out", detail: "Each item gets its own sticker." },
    { title: "The label is the item", detail: "Order number, item 2 of 4, variant, modifiers." },
    { title: "It lands at expo", detail: "Packing or the window, stuck on the bag or the cup." },
  ],
  "printer-scan": [
    {
      title: "Scan the barcode",
      detail: "An exact SKU adds the product. A customer code attaches them.",
    },
    { title: "It hits the check", detail: "No separate scanner. The printer reads it." },
    { title: "Then the label prints", detail: "Same item sticker as the 80mm, for packing." },
  ],
  "printer-58": [
    { title: "Close the check", detail: "Guest check before they pay, receipt after." },
    { title: "Both copies", detail: "Customer copy, and a merchant copy when the shop wants one." },
    { title: "Cash opens the drawer", detail: "Tip line and signature stay on the card receipt." },
  ],
  drawer: [
    { title: "Take cash", detail: "Closing the check as cash is what kicks it." },
    {
      title: "The drawer pops",
      detail: "From the register, or from a tablet over USB or Bluetooth.",
    },
    { title: "You can see it's open", detail: "Open and close status comes back to the POS." },
  ],
  scanner: [
    { title: "They hold the code up", detail: "The window sits at 45°, for paper or a phone." },
    { title: "It reads it", detail: "1D or 2D. A light and a beep when it lands." },
    { title: "It hits the check", detail: "A SKU adds the item. A customer code attaches them." },
  ],
  "scanner-2d": [
    {
      title: "Aim and scan",
      detail: "White light and a red aim. Screen, paper, or a wrinkled code.",
    },
    { title: "It confirms", detail: "Buzzer, a vibration, and a light." },
    {
      title: "It hits the check",
      detail: "Same as the counter scanner. The stand leaves both hands free.",
    },
  ],
  kds: [
    {
      title: "Tickets land as they're sent",
      detail: "From the register, the kiosk, tableside, or Uber Eats, DoorDash, and Grubhub.",
    },
    {
      title: "Counts stay live",
      detail:
        "Each item and each modifier, split into pending, preparing, and ready. Tap one to filter the board.",
    },
    {
      title: "Cancel a delivery order",
      detail:
        "Uber, DoorDash, and Grubhub can be canceled from the kitchen. Uber and DoorDash show the phone code.",
    },
  ],
  k2: [
    {
      title: "Name and mobile",
      detail: "That phone finds their contact, or creates one. That's the rewards account.",
    },
    {
      title: "They order and pay",
      detail: "Their menu, modifiers, a tip, then the card. Points land when it's paid.",
    },
    {
      title: "A text when it's ready",
      detail:
        "The kitchen ticket fires on payment. When the order is ready, that number gets the pickup text.",
    },
  ],
};

const DEVICE_FACTS: Record<string, string[]> = {
  "printer-kitchen": [
    "Same items group together",
    "Can split by course",
    "Modifiers can print bold",
    "Allergens stay on the ticket",
    "A reprint is marked a copy",
    "Delivery apps show the source",
  ],
  "printer-label": [
    "Item 2 of 4",
    "Name, qty, variant, modifiers",
    "Fixed sticker or linerless",
    "Packing and expo",
    "Prints with the order",
  ],
  "printer-scan": [
    "Exact barcode or SKU",
    "Customer code attaches them",
    "Then the item sticker prints",
    "Packing station",
  ],
  "printer-58": [
    "Customer and merchant copies",
    "Tip line and signature",
    "Logo and order number",
    "Cash kicks the drawer",
    "58mm labels too",
  ],
  drawer: [
    "5 bill trays",
    "4 fixed and 6 removable coin trays",
    "Check slot, without opening it",
    "Lock: software, key, or shut",
    "USB, Bluetooth, or RJ12",
    "9V, 12V, or 24V",
  ],
  scanner: ["Type-A or Type-C", "Plug into the terminal or a tablet", "1D and 2D", "Counter mount"],
  k2: [
    "A reward on the account comes off the check",
    "Percent tip, no tip, or a custom amount",
    "Cash price and card price",
    "Built-in reader, or a linked terminal",
    "Receipt on the kiosk, no merchant copy",
    "Estimated pickup on the thank-you screen",
    "Idle 25 seconds, then back to welcome",
  ],
  kds: [
    "Total, preparing, and pending",
    "All-day counts are ingredients",
    "Only the modifiers they picked",
    "Yellow, then red, by channel",
    "Scheduled orders wait to fire",
    "Recall a bumped ticket",
    "Expo is its own board",
  ],
  "scanner-2d": [
    "CMOS, white LED, red aim",
    "Payment codes and barcodes",
    "USB",
    "Hands-free stand",
  ],
};

const PAPER_TEAR =
  "polygon(0 0, 100% 0, 100% calc(100% - 3px), 96% 100%, 92% calc(100% - 3px), 88% 100%, 84% calc(100% - 3px), 80% 100%, 76% calc(100% - 3px), 72% 100%, 68% calc(100% - 3px), 64% 100%, 60% calc(100% - 3px), 56% 100%, 52% calc(100% - 3px), 48% 100%, 44% calc(100% - 3px), 40% 100%, 36% calc(100% - 3px), 32% 100%, 28% calc(100% - 3px), 24% 100%, 20% calc(100% - 3px), 16% 100%, 12% calc(100% - 3px), 8% 100%, 4% calc(100% - 3px), 0 100%)";

function PrinterSlip({ kind, name }: { kind: string; name: string }) {
  const mono = "font-mono text-[11px] leading-[15px] text-[#111318]";
  const rule = "my-1 border-t border-dashed border-[#c9d0dc]";
  if (kind === "printer-label" || kind === "printer-scan") {
    return (
      <div className="flex flex-col items-center gap-3">
        <div className="w-[168px] rounded-md border border-[#e6e9f0] bg-white px-3 py-2.5 shadow-md">
          <div className="text-center font-mono text-[16px] font-bold leading-none">#184</div>
          <div className="mt-1 text-center font-mono text-[10px] text-[#5b6472]">Item 2 / 4</div>
          <div className={rule} />
          <div className="font-mono text-[12px] font-bold leading-tight">SMASH BURGER</div>
          <div className="mt-1 font-mono text-[10px] leading-tight text-[#5b6472]">NO ONION</div>
        </div>
        {kind === "printer-scan" ? (
          <div className="flex items-center gap-2 rounded-full bg-[#111318] px-2.5 py-1 text-white">
            <span className="flex h-3 items-end gap-px" aria-hidden>
              <span className="h-2 w-px bg-white" />
              <span className="h-3 w-0.5 bg-white" />
              <span className="h-2.5 w-px bg-white" />
              <span className="h-3 w-[3px] bg-white" />
              <span className="h-2 w-px bg-white" />
              <span className="h-3 w-0.5 bg-white" />
              <span className="h-2 w-px bg-white" />
              <span className="h-3 w-[3px] bg-white" />
              <span className="h-2.5 w-0.5 bg-white" />
              <span className="h-2 w-px bg-white" />
            </span>
            <span className="text-[11px] font-semibold">Smash Burger added</span>
          </div>
        ) : null}
      </div>
    );
  }
  if (kind === "printer-58") {
    return (
      <div
        className={cn("w-[188px] bg-white px-3 pb-3 pt-2.5 shadow-md", mono)}
        style={{ clipPath: PAPER_TEAR }}
      >
        <div className="text-center text-[12px] font-bold">{name.toUpperCase()}</div>
        <div className="text-center text-[10px]">Guest check</div>
        <div className={rule} />
        <div className="flex justify-between">
          <span>Order #184</span>
          <span>Table 4</span>
        </div>
        <div className={rule} />
        <div className="flex justify-between gap-2">
          <span>1 Smash Burger</span>
          <span>14.00</span>
        </div>
        <div className="flex justify-between gap-2">
          <span>1 Fries</span>
          <span>5.00</span>
        </div>
        <div className={rule} />
        <div className="flex justify-between font-bold">
          <span>Total</span>
          <span>20.71</span>
        </div>
        <div className="mt-1 flex justify-between text-[10px]">
          <span>Tip</span>
          <span>______</span>
        </div>
        <div className="mt-1 text-[10px]">Sign ______________</div>
      </div>
    );
  }
  return (
    <div
      className={cn("w-[188px] bg-white px-3 pb-3 pt-2.5 shadow-md", mono)}
      style={{ clipPath: PAPER_TEAR }}
    >
      <div className="text-center text-[12px] font-bold">{name.toUpperCase()}</div>
      <div className="text-center font-bold">KITCHEN</div>
      <div className={rule} />
      <div className="flex justify-between">
        <span>TABLE 4</span>
        <span>#184</span>
      </div>
      <div className="flex justify-between">
        <span>Alex</span>
        <span>Dine-in</span>
      </div>
      <div className={rule} />
      <div className="font-bold">2 SMASH BURGER</div>
      <div className="pl-3 text-[10px]">+ No onion</div>
      <div className="font-bold">1 FRIES</div>
      <div className={rule} />
      <div className="text-center">3 ITEMS</div>
    </div>
  );
}

function DrawerOpen() {
  const bills = ["100", "50", "20", "10", "5"];
  const coins = ["25¢", "10¢", "5¢", "$1"];
  return (
    <div className="w-[220px] rounded-xl bg-[#1c1f26] p-3 text-white shadow-md">
      <div className="flex items-center justify-between text-[10px] font-semibold tracking-wide">
        <span>MAX</span>
        <span className="text-[#ff6a2a]">OPEN</span>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1">
        {bills.map((bill) => (
          <div key={bill} className="rounded bg-white/10 py-3 text-center font-mono text-[10px]">
            {bill}
          </div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-5 gap-1">
        {coins.map((coin) => (
          <div
            key={coin}
            className="rounded-full bg-white/10 py-1.5 text-center font-mono text-[9px]"
          >
            {coin}
          </div>
        ))}
        <div className="rounded bg-[#ff6a2a]/20 py-1.5 text-center font-mono text-[9px] text-[#ffb088]">
          slip
        </div>
      </div>
      <div className="mt-2 text-[10px] text-white/60">Check slot stays shut</div>
    </div>
  );
}

function ScanHit() {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-2 rounded-full bg-[#111318] px-2.5 py-1 text-white">
        <span className="flex h-3 items-end gap-px" aria-hidden>
          <span className="h-2 w-px bg-white" />
          <span className="h-3 w-0.5 bg-white" />
          <span className="h-2.5 w-px bg-white" />
          <span className="h-3 w-[3px] bg-white" />
          <span className="h-2 w-px bg-white" />
          <span className="h-3 w-0.5 bg-white" />
          <span className="h-2 w-px bg-white" />
          <span className="h-3 w-[3px] bg-white" />
          <span className="h-2.5 w-0.5 bg-white" />
          <span className="h-2 w-px bg-white" />
        </span>
        <span className="text-[11px] font-semibold">Smash Burger added</span>
      </div>
      <div className="rounded-full border border-[#e6e9f0] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#111318] shadow-sm">
        Alex · on the check
      </div>
    </div>
  );
}

function KioskMoment() {
  return (
    <div className="flex w-[240px] flex-col gap-2">
      <div className="rounded-xl bg-white p-3 shadow-md">
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#8b93a1]">
          Let's get started
        </div>
        <div className="mt-2 rounded-lg border border-[#e6e9f0] px-2.5 py-1.5 text-[12px] font-semibold">
          Alex
        </div>
        <div className="mt-1.5 rounded-lg border border-[#e6e9f0] px-2.5 py-1.5 text-[12px] font-semibold">
          (312) 555-0148
        </div>
        <div className="mt-2 flex items-center justify-between rounded-full bg-[#111318] px-2.5 py-1 text-white">
          <span className="text-[11px] font-semibold">Rewards</span>
          <span className="text-[11px] font-bold">240 pts</span>
        </div>
      </div>
      <div className="rounded-xl bg-[#111318] px-3 py-2 text-white shadow-md">
        <div className="text-[10px] font-semibold text-white/60">Text</div>
        <div className="mt-0.5 text-[12px] font-semibold leading-snug">
          Order #184 is ready for pickup
        </div>
      </div>
    </div>
  );
}

function KdsBoard() {
  const counts = [
    { label: "Smash Burger", qty: "6", mod: false },
    { label: "No onion", qty: "4", mod: true },
    { label: "Fries", qty: "3", mod: false },
  ];
  return (
    <div className="flex items-stretch gap-2">
      <div className="w-[156px] overflow-hidden rounded-lg bg-white shadow-md">
        <div className="bg-[#f59e0b] px-2 py-1.5 text-white">
          <div className="flex items-center justify-between text-[11px] font-bold">
            <span>#184</span>
            <span className="rounded bg-white/25 px-1 text-[9px]">Uber Eats</span>
          </div>
          <div className="mt-0.5 font-mono text-[10px]">CODE 482 19</div>
        </div>
        <div className="space-y-0.5 px-2 py-1.5 font-mono text-[10px] leading-4 text-[#111318]">
          <div className="font-bold">2 Smash Burger</div>
          <div className="pl-2 text-[#5b6472]">No onion ×2</div>
          <div className="font-bold">1 Fries</div>
        </div>
        <div className="px-2 pb-2">
          <span className="rounded-full bg-[#111318] px-2 py-0.5 text-[9px] font-semibold text-white">
            Cancel order
          </span>
        </div>
      </div>
      <div className="w-[132px] rounded-lg bg-white p-2 shadow-md">
        <div className="text-[9px] font-bold uppercase tracking-wide text-[#8b93a1]">
          Dish counts
        </div>
        <div className="mt-1.5 space-y-1">
          {counts.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-2 text-[10px]">
              <span className={row.mod ? "pl-2 text-[#5b6472]" : "font-semibold"}>{row.label}</span>
              <span className="rounded-full bg-[#f2f4f8] px-1.5 font-bold">{row.qty}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-1 text-[9px] font-semibold">
          <span className="rounded bg-[#f2f4f8] px-1.5 py-0.5">4 pending</span>
          <span className="rounded bg-[#fff4d6] px-1.5 py-0.5">3 prep</span>
        </div>
      </div>
    </div>
  );
}

/** Catalog entries that are a real screen on the floor: the category tab shows that live screen. */
const FLOOR_ROLE: Partial<Record<(typeof DEVICES)[number]["key"], FloorRole>> = {
  d3: "register",
  p3h: "handheld",
  cpad: "handheld",
  k2: "kiosk",
  kds: "kds",
  cfd: "cfd",
};

function DeviceGlyph({
  art,
  photo,
}: {
  art: (typeof DEVICES)[number]["art"];
  photo?: string;
}) {
  if (photo) {
    return <img src={photo} alt="" className="h-full w-full object-contain" />;
  }
  return <DeviceArt kind={art} />;
}

export function HardwareSection({ b, d }: { b: Brand; d: PortalDTO }) {
  const [group, setGroup] = useState("All Devices");
  const [sel, setSel] = useState<(typeof DEVICES)[number]["key"]>("d3");
  const [done, setDone] = useState(new Set<string>());
  const inGroup = DEVICES.filter((d) => d.group === group);
  // The side list always shows every device, so a category tab doesn't hide the rest.
  const picked = DEVICES.find((d) => d.key === sel) ?? inGroup[0];
  const dev = picked ?? DEVICES[0];
  const mark = (a: string) => setDone((s) => new Set(s).add(a));
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-1 rounded-lg border border-[#e6e9f0] bg-white p-1">
        {GROUPS.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => {
              setGroup(g);
              const first = DEVICES.find((item) => item.group === g);
              if (first) setSel(first.key);
            }}
            className={cn(
              "rounded-md px-4 py-1.5 text-[13px] font-semibold",
              group === g ? "bg-[#2563eb] text-white" : "text-[#5b6472] hover:bg-[#f7f8fb]",
            )}
          >
            {g}
          </button>
        ))}
      </div>
      {group === "All Devices" ? (
        <HardwareFloor b={b} d={d} />
      ) : (
        <div className="grid gap-5 xl:grid-cols-[230px_1fr_280px]">
          <div className="max-h-[calc(100vh-8rem)] space-y-3 overflow-y-auto pr-1">
            {GROUPS.filter((g) => g !== "All Devices").map((g) => (
              <div key={g} className="space-y-1.5">
                <div className="px-1 text-[10px] font-semibold uppercase tracking-wide text-[#8b93a1]">
                  {g}
                </div>
                {g === "Printers" ? (
                  <div className="overflow-hidden rounded-xl border border-[#e6e9f0] bg-white">
                    {PRINTER_FAMILIES.map((family) => (
                      <div
                        key={family.label}
                        className="border-t border-[#e6e9f0] first:border-t-0"
                      >
                        <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[#8b93a1]">
                          {family.label}
                        </div>
                        {family.keys.map((key) => {
                          const dv = DEVICES.find((item) => item.key === key);
                          if (!dv) return null;
                          const on = sel === dv.key && group === g;
                          return (
                            <button
                              key={dv.key}
                              type="button"
                              onClick={() => {
                                setGroup(g);
                                setSel(dv.key);
                              }}
                              className={cn(
                                "flex w-full items-center gap-3 px-2.5 py-2 text-left",
                                on ? "bg-[#eaf0ff]" : "hover:bg-[#f7f8fb]",
                              )}
                            >
                              <span className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-[#f2f4f8] p-1">
                                <DeviceGlyph
                                  art={dv.art}
                                  photo={"photo" in dv ? dv.photo : undefined}
                                />
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-[13px] font-bold">
                                  {dv.name}
                                </span>
                                <span className="block truncate text-[11px] text-[#5b6472]">
                                  {dv.sub}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : g === "Accessories" ? (
                  <>
                    {DEVICES.filter((d) => d.group === g && !SCANNER_KEYS.includes(d.key)).map(
                      (dv) => (
                        <button
                          key={dv.key}
                          type="button"
                          onClick={() => {
                            setGroup(g);
                            setSel(dv.key);
                          }}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-xl border bg-white p-2.5 text-left",
                            sel === dv.key && group === g
                              ? "border-[#2563eb] ring-2 ring-[#2563eb]/15"
                              : "border-[#e6e9f0] hover:bg-[#f7f8fb]",
                          )}
                        >
                          <span className="flex h-12 w-14 shrink-0 items-center justify-center rounded-lg bg-[#f2f4f8] p-1">
                            <DeviceGlyph
                              art={dv.art}
                              photo={"photo" in dv ? dv.photo : undefined}
                            />
                          </span>
                          <span>
                            <span className="block text-[13px] font-bold">{dv.name}</span>
                            <span className="block text-[11px] text-[#5b6472]">{dv.sub}</span>
                          </span>
                        </button>
                      ),
                    )}
                    <div className="overflow-hidden rounded-xl border border-[#e6e9f0] bg-white">
                      <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-[#8b93a1]">
                        Scanners
                      </div>
                      {SCANNER_KEYS.map((key) => {
                        const dv = DEVICES.find((item) => item.key === key);
                        if (!dv) return null;
                        const on = sel === dv.key && group === g;
                        return (
                          <button
                            key={dv.key}
                            type="button"
                            onClick={() => {
                              setGroup(g);
                              setSel(dv.key);
                            }}
                            className={cn(
                              "flex w-full items-center gap-3 px-2.5 py-2 text-left",
                              on ? "bg-[#eaf0ff]" : "hover:bg-[#f7f8fb]",
                            )}
                          >
                            <span className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-[#f2f4f8] p-1">
                              <DeviceGlyph
                                art={dv.art}
                                photo={"photo" in dv ? dv.photo : undefined}
                              />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-[13px] font-bold">
                                {dv.name}
                              </span>
                              <span className="block truncate text-[11px] text-[#5b6472]">
                                {dv.sub}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  DEVICES.filter((d) => d.group === g).map((dv) => (
                    <button
                      key={dv.key}
                      type="button"
                      onClick={() => {
                        setGroup(g);
                        setSel(dv.key);
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border bg-white p-2.5 text-left",
                        sel === dv.key && group === g
                          ? "border-[#2563eb] ring-2 ring-[#2563eb]/15"
                          : "border-[#e6e9f0] hover:bg-[#f7f8fb]",
                      )}
                    >
                      <span className="flex h-12 w-14 shrink-0 items-center justify-center rounded-lg bg-[#f2f4f8] p-1">
                        <DeviceGlyph art={dv.art} photo={"photo" in dv ? dv.photo : undefined} />
                      </span>
                      <span>
                        <span className="block text-[13px] font-bold">{dv.name}</span>
                        <span className="block text-[11px] text-[#5b6472]">{dv.sub}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            ))}
          </div>
          <div>
            {group === "Printers" ? (
              <Card className="p-5">
                <div className="flex flex-wrap items-end justify-center gap-x-10 gap-y-6">
                  {PRINTER_FAMILIES.map((family) => (
                    <div key={family.label}>
                      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#8b93a1]">
                        {family.label}
                      </div>
                      <div className="flex items-end gap-2">
                        {family.keys.map((key) => {
                          const dv = DEVICES.find((item) => item.key === key);
                          if (!dv || !("photo" in dv)) return null;
                          const on = sel === dv.key;
                          return (
                            <button
                              key={dv.key}
                              type="button"
                              onClick={() => setSel(dv.key)}
                              className={cn(
                                "rounded-xl px-2 py-2 text-center transition-[width,background-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                                on
                                  ? "w-[240px] bg-[#eaf0ff] ring-2 ring-[#2563eb]"
                                  : "w-[150px] hover:bg-[#f7f8fb]",
                              )}
                            >
                              <img
                                src={dv.photo}
                                alt=""
                                className={cn(
                                  "mx-auto w-full object-contain transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                                  on ? "h-52" : "h-28",
                                )}
                              />
                              <div className="mt-2 text-[12px] font-bold leading-tight">
                                {dv.name}
                              </div>
                              <div className="text-[10px] text-[#5b6472]">{dv.sub}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : group === "Accessories" && SCANNER_KEYS.includes(dev.key) ? (
              <Card className="p-5">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#8b93a1]">
                  Scanners
                </div>
                <div className="flex flex-wrap items-end justify-center gap-2">
                  {SCANNER_KEYS.map((key) => {
                    const dv = DEVICES.find((item) => item.key === key);
                    if (!dv || !("photo" in dv)) return null;
                    const on = sel === dv.key;
                    return (
                      <button
                        key={dv.key}
                        type="button"
                        onClick={() => setSel(dv.key)}
                        className={cn(
                          "rounded-xl px-2 py-2 text-center transition-[width,background-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                          on
                            ? "w-[240px] bg-[#eaf0ff] ring-2 ring-[#2563eb]"
                            : "w-[150px] hover:bg-[#f7f8fb]",
                        )}
                      >
                        <img
                          src={dv.photo}
                          alt=""
                          className={cn(
                            "mx-auto w-full object-contain transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                            on ? "h-52" : "h-28",
                          )}
                        />
                        <div className="mt-2 text-[12px] font-bold leading-tight">{dv.name}</div>
                        <div className="text-[10px] text-[#5b6472]">{dv.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </Card>
            ) : FLOOR_ROLE[dev.key] ? (
              // The same screen as on the floor: a rep can start it on a real emulator here too.
              <HardwareFloor
                b={b}
                d={d}
                only={FLOOR_ROLE[dev.key]}
                shell={dev.key === "cpad" ? "cpad" : dev.key === "p3h" ? "p3h" : undefined}
              />
            ) : (
              <Card className="flex min-h-[380px] items-center justify-center p-8">
                {"photo" in dev && dev.photo ? (
                  <img
                    src={dev.photo}
                    alt={dev.name}
                    className="max-h-[420px] w-full max-w-[640px] object-contain"
                  />
                ) : (
                  <div className="h-48 w-64">
                    <DeviceArt kind={dev.art} />
                  </div>
                )}
              </Card>
            )}
            {DEVICE_STORY[dev.key] ? (
              <Card key={dev.key} className="mt-5 overflow-hidden p-0">
                <style>
                  {
                    "@keyframes mmPaperIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}"
                  }
                </style>
                <div className="flex flex-wrap items-stretch">
                  <div className="flex min-h-[220px] min-w-[220px] flex-1 items-center justify-center bg-[#f7f8fb] px-6 py-6">
                    <div
                      style={{ animation: "mmPaperIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both" }}
                    >
                      {dev.key === "drawer" ? (
                        <DrawerOpen />
                      ) : dev.key === "scanner" || dev.key === "scanner-2d" ? (
                        <ScanHit />
                      ) : dev.key === "kds" ? (
                        <KdsBoard />
                      ) : dev.key === "k2" ? (
                        <KioskMoment />
                      ) : (
                        <PrinterSlip kind={dev.key} name={b.name} />
                      )}
                    </div>
                  </div>
                  <div className="min-w-[260px] flex-[1.2] p-5">
                    <div className="text-[18px] font-bold">{dev.name}</div>
                    <div className="mt-0.5 text-[13px] text-[#5b6472]">{dev.blurb}</div>
                    <ol className="mt-4 space-y-3">
                      {(DEVICE_STORY[dev.key] ?? []).map((step, i) => (
                        <li key={step.title} className="flex gap-3">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#111318] text-[11px] font-bold text-white">
                            {i + 1}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-[13px] font-semibold">{step.title}</span>
                            <span className="block text-[12px] leading-snug text-[#5b6472]">
                              {step.detail}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ol>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(DEVICE_FACTS[dev.key] ?? []).map((fact) => (
                        <span
                          key={fact}
                          className="rounded-full bg-[#f2f4f8] px-2.5 py-1 text-[11px] font-medium text-[#3c4453]"
                        >
                          {fact}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="mt-5 flex flex-wrap items-center gap-6 p-5">
                <div className="h-20 w-28">
                  <DeviceGlyph art={dev.art} photo={"photo" in dev ? dev.photo : undefined} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[20px] font-bold">{dev.name}</div>
                  <div className="text-[13px] text-[#5b6472]">{dev.blurb}</div>
                  <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                    {dev.specs.map((s) => (
                      <li key={s} className="flex items-center gap-2 text-[13px]">
                        <CheckCircle2 className="h-4 w-4 text-[#16a34a]" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <BlueButton onClick={() => setSel(dev.key)}>
                  Try It Now <ArrowRight className="h-4 w-4" />
                </BlueButton>
              </Card>
            )}
          </div>
          <div className="space-y-4">
            <FeatureList
              title="Try It Out"
              picked={done}
              items={[
                {
                  icon: "add",
                  title: "Add items to order",
                  detail: "Click on menu items",
                  key: "add_item",
                },
                {
                  icon: "discount",
                  title: "Apply discounts",
                  detail: "Try different payment types",
                  key: "discount",
                },
                {
                  icon: "split",
                  title: "Split payment",
                  detail: "Cash, card, or mobile pay",
                  key: "pay",
                },
                {
                  icon: "kitchen",
                  title: "Send to kitchen",
                  detail: "See how orders are managed",
                  key: "pay",
                },
                {
                  icon: "print",
                  title: "Print receipt",
                  detail: "Explore sales and analytics",
                  key: "pay",
                },
              ]}
            />
            <div className="rounded-2xl border border-[#e6e9f0] bg-white p-4">
              <div className="flex gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eaf0ff] text-[#2563eb]">
                  <MousePointerClick className="h-4 w-4" />
                </span>
                <p className="text-[12px] text-[#5b6472]">
                  <span className="font-semibold text-[#111318]">This is a live simulator.</span>{" "}
                  Explore all the features! Your rep can also drive the real device for you on a
                  call.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Mobile */

/**
 * Three apps, three audiences: the owner runs the business from the Admin app,
 * customers find them (and every other MyManager business) in MyManager
 * Connect, and the White-label app is Connect reskinned as *their* app on the
 * stores. Every screen is built from the imported menu, hours and locations.
 */
// Two apps: the owner's Admin app and MyManager Connect. (The white-label app was
// dropped from the simulator on 2026-09-23; it is sold separately.)
type AppId = "admin" | "connect";
const APPS: {
  id: AppId;
  label: string;
  icon: typeof Globe;
  who: string;
  blurb: string;
  screens: [string, typeof Globe][];
}[] = [
  {
    id: "admin",
    label: "Admin app",
    icon: Monitor,
    who: "For you and your team",
    blurb: "Run the business from your pocket: live orders, bookings, customers and reports.",
    screens: [
      ["Dashboard", BarChart3],
      ["Orders", ShoppingCart],
      ["Reports", TrendingUp],
    ],
  },
  {
    id: "connect",
    label: "Connect app",
    icon: Search,
    who: "For customers, on the app stores today",
    blurb: "MyManager Connect lists your business next to every other one on the platform.",
    screens: [
      ["Discover", Search],
      ["Your listing", Star],
      ["Wallet", Gift],
    ],
  },
];

const FEATURES: Record<AppId, { icon: string; title: string; detail: string }[]> = {
  admin: [
    {
      icon: "ordering",
      title: "Live orders",
      detail: "Accept, prepare and complete from anywhere",
    },
    {
      icon: "reservations",
      title: "Bookings & tables",
      detail: "Today's reservations at a glance",
    },
    {
      icon: "users",
      title: "Customers",
      detail: "Who's ordering, who's slipping away",
    },
    {
      icon: "chart",
      title: "Sales & reports",
      detail: "Per location, per item, per hour",
    },
    {
      icon: "notifications",
      title: "Alerts",
      detail: "New order, low stock, a review came in",
    },
  ],
  connect: [
    {
      icon: "seo",
      title: "Get discovered",
      detail: "Listed for customers searching nearby",
    },
    {
      icon: "ordering",
      title: "Order & book",
      detail: "Same menu and calendar as your site",
    },
    {
      icon: "loyalty",
      title: "Shared wallet",
      detail: "Points, gift cards and memberships",
    },
    {
      icon: "notifications",
      title: "Reach them",
      detail: "Push offers to people who follow you",
    },
    {
      icon: "check",
      title: "No build needed",
      detail: "Live the day your demo goes live",
    },
  ],
};

const EMULATOR_APP: Record<AppId, EmulatorApp> = {
  admin: "admin",
  connect: "client",
};

export function MobileSection({
  b,
  d,
  token,
}: {
  b: Brand;
  d: PortalDTO;
  token: string;
}) {
  const [app, setApp] = useState<AppId>("admin");
  const [screen, setScreen] = useState(0);
  const def = APPS.find((a) => a.id === app)!;
  const live = useLiveSlot("phone");
  // Rep signed in: every app button starts/switches the real device. Prospect: mocks.
  const liveApp = live.session
    ? (Object.keys(EMULATOR_APP) as AppId[]).find((k) => EMULATOR_APP[k] === live.session!.app)
    : undefined;
  const shownApp = live.enabled && liveApp ? liveApp : app;
  const pick = (id: AppId) => {
    setApp(id);
    setScreen(0);
    if (live.enabled) live.launch(EMULATOR_APP[id]);
  };
  // One phone. Every app runs on the same device, like the real emulator does.
  const current: ReactNode =
    app === "admin"
      ? [
          <PhoneAdminHome key="ah" b={b} />,
          <PhoneAdminOrders key="ao" b={b} />,
          <PhoneAdminReports key="ar" b={b} />,
        ][screen]
      : [
          <PhoneConnectDiscover key="cd" b={b} />,
          <PhoneConnectBusiness key="cb" b={b} />,
          <PhoneConnectWallet key="cw" b={b} />,
        ][screen];
  return (
    <div>
      <PageTitle
        title="Mobile App Simulator"
        sub={
          live.enabled
            ? "One phone, two apps — the real apps on a live device. Pick an app to run it."
            : "One phone, two apps. Pick an app, then tap through its screens."
        }
      />
      <div className="grid gap-5 xl:grid-cols-[220px_1fr_260px]">
        <div className="space-y-2">
          {APPS.map((a) => (
            <button
              key={a.id}
              type="button"
              disabled={live.busy}
              onClick={() => pick(a.id)}
              className={cn(
                "w-full rounded-xl border px-3 py-2.5 text-left disabled:opacity-60",
                a.id === shownApp
                  ? "border-[#2563eb] bg-[#eaf0ff]"
                  : "border-[#e6e9f0] bg-white hover:bg-[#f7f8fb]",
              )}
            >
              <div
                className={cn(
                  "flex items-center gap-2 text-[13px] font-semibold",
                  a.id === shownApp ? "text-[#2563eb]" : "text-[#111318]",
                )}
              >
                <a.icon className="h-4 w-4" /> {a.label}
              </div>
              <div className="mt-0.5 text-[11px] text-[#5b6472]">{a.who}</div>
            </button>
          ))}
          <div className="rounded-xl border border-[#e6e9f0] bg-white p-3 text-[12px] text-[#5b6472]">
            {def.blurb}
          </div>
          <DemoAccountCard d={d} token={token} />
        </div>
        <div className="flex flex-col items-center gap-4">
          {live.enabled ? (
            live.session && liveApp ? (
              <DeviceFrame frame="phone" session={live.session} maxHeight="min(76vh, 56rem)" />
            ) : (
              <div className="flex h-[560px] w-[318px] flex-col items-center justify-center gap-3 rounded-[28px] border-[10px] border-[#111318] bg-[#111318] text-center text-white">
                {live.busy ? (
                  <SwitchFactsLoader />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => live.launch(EMULATOR_APP[app])}
                      className="flex items-center gap-2 rounded-full bg-[#2563eb] px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-[#1d4fd8]"
                    >
                      <Play className="h-4 w-4" /> Start {def.label}
                    </button>
                    <div className="px-6 text-[12px] text-white/60">
                      Starts the real {def.label.toLowerCase()} on the live device. Pick the other
                      app on the left to run that one instead.
                    </div>
                  </>
                )}
              </div>
            )
          ) : (
            <div className="h-[676px] w-[318px]">
              <div className="origin-top-left scale-[1.3]">
                <PhoneFrame key={`${app}-${screen}`}>{current}</PhoneFrame>
              </div>
            </div>
          )}
          <LiveBar apps={["admin", "client"]} purpose="phone" />
          <div className={cn("flex flex-wrap justify-center gap-1.5", live.enabled && "hidden")}>
            {def.screens.map(([l, I], i) => (
              <button
                key={l}
                type="button"
                onClick={() => setScreen(i)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold",
                  i === screen
                    ? "border-[#2563eb] bg-[#2563eb] text-white"
                    : "border-[#e6e9f0] bg-white text-[#5b6472] hover:bg-[#f7f8fb]",
                )}
              >
                <I className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>
          {!live.enabled ? (
            <p className="max-w-md text-center text-[12px] text-[#5b6472]">
              This is a simulator built from your imported menu, hours and locations.
              {d.demo.status === "active"
                ? " On a call, your rep streams the real apps to this same phone from a live device."
                : ""}
            </p>
          ) : null}
        </div>
        <FeatureList title={`${def.label} features`} items={FEATURES[app]} />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- Online shop */

/**
 * Their real online store, live from the demo organization: the ShopV2
 * storefront on the demo client, one per location. Not a mock — orders placed
 * here land in the demo's admin app and POS.
 */
export function OnlineShopSection({ b, d }: { b: Brand; d: PortalDTO }) {
  const shops = d.locations.filter((l) => l.onlineShopUrl).length
    ? d.locations
        .filter((l) => l.onlineShopUrl)
        .map((l) => ({ name: l.name, url: l.onlineShopUrl as string }))
    : d.demo.onlineShopUrl
      ? [{ name: b.name, url: d.demo.onlineShopUrl }]
      : [];
  const [i, setI] = useState(0);
  const cur = shops[i] ?? shops[0];
  // What the prospect sees in the fake address bar: their org's own subdomain (the server
  // creates the demo org at demo-<business>-<tag>), not the Cloud Run host that actually serves it.
  // The demo org's own branded host (https://<slug>-demo.mymanager.com) is the origin of
  // websiteUrl; the address bar shows the shop there. Older demos on the raw Cloud Run host
  // fall back to the url as given.
  const origin = (() => {
    try {
      return d.demo.websiteUrl ? new URL(d.demo.websiteUrl).origin : null;
    } catch {
      return null;
    }
  })();
  const shown = (s: { name: string; url: string }) =>
    origin
      ? `${origin}/online-shop${shops.length > 1 ? `/${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""}`
      : s.url;
  return (
    <div>
      <PageTitle
        title="Online Shop"
        sub="Your real online store, live from the demo — menu, cart and checkout as your customers would use it."
        right={
          cur ? (
            <a
              href={cur.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-[#e6e9f0] bg-white px-3 py-2 text-[13px] font-semibold text-[#2563eb] hover:bg-[#f7f8fb]"
            >
              Open in a new tab <ExternalLink className="h-4 w-4" />
            </a>
          ) : undefined
        }
      />
      {shops.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {shops.map((s, idx) => (
            <button
              key={s.url}
              type="button"
              onClick={() => setI(idx)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-[12px] font-semibold",
                idx === i
                  ? "border-[#2563eb] bg-[#2563eb] text-white"
                  : "border-[#e6e9f0] bg-white text-[#5b6472] hover:bg-[#f7f8fb]",
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
      ) : null}
      {cur ? (
        <BrowserChrome url={shown(cur)}>
          <iframe
            key={cur.url}
            title={`${cur.name} online shop`}
            src={cur.url}
            className="h-[76vh] min-h-[560px] w-full border-0 bg-white"
            allow="clipboard-write"
          />
        </BrowserChrome>
      ) : (
        <Card className="p-8 text-center text-[14px] text-[#5b6472]">
          {d.demo.status === "active"
            ? "The online shop is being prepared. Refresh in a moment."
            : "The online shop appears here once the demo is live."}
        </Card>
      )}
      <p className="mt-3 text-[12px] text-[#5b6472]">
        Payments, texts and emails are switched off in the demo; everything else is the real thing.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------- Presentation */

export function PresentationSection({ b, d }: { b: Brand; d: PortalDTO }) {
  const [i, setI] = useState(0);
  const slides = d.presentation;
  const s = slides[i] ?? slides[0];
  if (!s) return null;
  const img = s.imageUrl ?? b.hero ?? b.images[0];
  return (
    <div>
      <PageTitle
        title="Business Review & Opportunities"
        sub="See what we found and how we can help."
      />
      <div className="grid gap-5 xl:grid-cols-[240px_1fr]">
        <ol className="space-y-1.5">
          {slides.map((sl, idx) => (
            <li key={sl.id}>
              <button
                type="button"
                onClick={() => setI(idx)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-[13px] font-semibold",
                  idx === i
                    ? "border-[#2563eb] bg-[#eaf0ff] text-[#2563eb]"
                    : "border-[#e6e9f0] bg-white text-[#5b6472] hover:bg-[#f7f8fb]",
                )}
              >
                <span
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold",
                    idx === i ? "bg-[#2563eb] text-white" : "bg-[#f0f2f7] text-[#5b6472]",
                  )}
                >
                  {idx + 1}
                </span>
                {sl.title}
              </button>
            </li>
          ))}
        </ol>
        <Card className="p-6">
          <h2 className="text-[22px] font-bold">
            {i + 1}. {s.title}
          </h2>
          <p className="text-[14px] text-[#5b6472]">{s.subtitle}</p>
          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_1.1fr]">
            <div className="relative min-h-[240px] overflow-hidden rounded-xl bg-[#0f172a]">
              {img ? (
                <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : null}
              {s.quote ? (
                <div className="absolute inset-x-4 bottom-4 rounded-xl bg-white/95 p-4 shadow-lg">
                  <div className="text-[28px] leading-none text-[#2563eb]">“</div>
                  <p className="text-[14px] font-semibold text-[#111318]">{s.quote}</p>
                  <div className="mt-1 text-[11px] text-[#5b6472]">— {s.quoteBy}</div>
                </div>
              ) : null}
            </div>
            <ul className="space-y-3">
              {s.points.map((p, idx) => {
                const Icon = ICONS[p.icon ?? ""] ?? Sparkles;
                const [bg, fg] = tone(idx);
                return (
                  <li key={p.title} className="flex gap-3 rounded-xl border border-[#e6e9f0] p-3">
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: bg, color: fg }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold">{p.title}</div>
                      <div className="text-[12px] text-[#5b6472]">{p.detail}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              disabled={i === 0}
              onClick={() => setI(i - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e6e9f0] text-[#5b6472] disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[13px] text-[#5b6472]">
              {i + 1} / {slides.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={i === slides.length - 1}
                onClick={() => setI(i + 1)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e6e9f0] text-[#5b6472] disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {i < slides.length - 1 ? (
                <BlueButton onClick={() => setI(i + 1)}>
                  Next <ArrowRight className="h-4 w-4" />
                </BlueButton>
              ) : (
                <BlueButton href={`mailto:${b.agent.email}`}>
                  Let's get started <ArrowRight className="h-4 w-4" />
                </BlueButton>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Info */

const CUISINES = [
  "Italian",
  "Mexican",
  "American",
  "Asian",
  "Mediterranean",
  "Pizza",
  "Cafe & Bakery",
  "Bar & Grill",
  "Other",
];

export function InfoSection({
  b,
  d,
  token,
}: {
  b: Brand;
  d: PortalDTO;
  token: string;
}) {
  const [f, setF] = useState({
    businessName: b.name,
    contactName: `${d.prospect.contact.firstName} ${d.prospect.contact.lastName}`.trim(),
    phone: b.phone,
    email: d.prospect.contact.email,
    address: b.address,
    cuisine: b.cuisine,
    website: b.website,
    locationsCount: b.locationsCount,
  });
  const save = useMutation({
    mutationFn: () => {
      const [firstName, ...rest] = f.contactName.split(" ");
      return api.post(`/v1/portal/${token}/customer-info`, {
        businessName: f.businessName,
        cuisine: f.cuisine,
        website: f.website,
        locationsCount: Number(f.locationsCount) || 1,
        address: f.address,
        contact: {
          firstName: firstName ?? "",
          lastName: rest.join(" "),
          email: f.email,
          phone: f.phone,
        },
      });
    },
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value });
  const F = ({
    label,
    children,
    className,
  }: {
    label: string;
    children: ReactNode;
    className?: string;
  }) => (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children
    <label className={cn("block", className)}>
      <span className="mb-1 block text-[12px] font-semibold text-[#111318]">{label}</span>
      {children}
    </label>
  );
  const inp =
    "h-10 w-full rounded-lg border border-[#dfe3ea] bg-white px-3 text-[13px] outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/15";
  return (
    <div>
      <PageTitle
        title="Customer Information"
        sub="Review and update your restaurant information."
      />
      <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
        <Card className="p-6">
          <div className="mb-4 text-[16px] font-bold">Restaurant Information</div>
          <div className="grid gap-4 md:grid-cols-3">
            <F label="Restaurant Name">
              <input className={inp} value={f.businessName} onChange={set("businessName")} />
            </F>
            <F label="Contact Name">
              <input className={inp} value={f.contactName} onChange={set("contactName")} />
            </F>
            <F label="Email">
              <input className={inp} value={f.email} onChange={set("email")} />
            </F>
            <F label="Phone" className="md:col-span-2">
              <input className={inp} value={f.phone} onChange={set("phone")} />
            </F>
            <F label="Cuisine">
              <select className={inp} value={f.cuisine} onChange={set("cuisine")}>
                {[...new Set([f.cuisine, ...CUISINES].filter(Boolean))].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </F>
            <F label="Address" className="md:col-span-2">
              <input
                className={inp}
                value={f.address}
                onChange={set("address")}
                placeholder="123 Main St, City, ST 00000"
              />
            </F>
            <F label="Website (optional)">
              <input className={inp} value={f.website} onChange={set("website")} />
            </F>
            <F label="Number of Locations" className="md:col-span-2">
              <input
                className={inp}
                type="number"
                min={1}
                value={f.locationsCount}
                onChange={set("locationsCount")}
              />
            </F>
            <div className="flex items-end">
              <BlueButton onClick={() => save.mutate()} className="w-full">
                {save.isPending ? "Saving…" : "Save Changes"}
              </BlueButton>
            </div>
          </div>
          {save.isSuccess ? (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#bbf7d0] bg-[#f0fdf4] p-3">
              <CheckCircle2 className="h-5 w-5 text-[#16a34a]" />
              <div>
                <div className="text-[13px] font-semibold text-[#14532d]">
                  Your information is saved!
                </div>
                <div className="text-[12px] text-[#3f6212]">
                  You can update your details anytime during your demo.
                </div>
              </div>
            </div>
          ) : null}
          {save.isError ? (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-[13px] text-red-700">
              Couldn't save. Please try again.
            </div>
          ) : null}
        </Card>
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#eaf0ff] text-[#2563eb]">
                <UserCircle2 className="h-5 w-5" />
              </span>
              <div>
                <div className="text-[15px] font-bold">Keep Your Info Up to Date</div>
                <p className="text-[12px] text-[#5b6472]">
                  This information helps us customize your demo experience. You can update it
                  anytime while in demo mode.
                </p>
              </div>
            </div>
            {b.hero ? (
              <img
                src={b.images[0] ?? b.hero}
                alt=""
                className="mt-4 h-44 w-full rounded-xl object-cover"
              />
            ) : null}
          </Card>
          <Card className="p-5">
            <div className="text-[13px] font-bold">Where this came from</div>
            <p className="mt-1 text-[12px] text-[#5b6472]">
              {d.shop?.provenance
                ? `Imported from ${labelFor(d.shop.provenance.platform)}${d.shop.provenance.sourceUrl ? "" : ""} on ${new Date(d.shop.provenance.fetchedAt).toLocaleDateString()}.`
                : "Entered by your MyManager rep."}
              {d.shop
                ? ` ${d.shop.items.length} menu items · ${d.shop.images.length} photos${d.shop.hours.length ? " · hours" : ""}.`
                : ""}
            </p>
            <div className="mt-3 flex items-center gap-2 text-[12px] text-[#5b6472]">
              <Clock className="h-4 w-4" /> Your rep: {b.agent.name}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const labelFor = (p: string) =>
  ({
    ubereats: "Uber Eats",
    doordash: "DoorDash",
    grubhub: "Grubhub",
    google_places: "Google Business Profile",
    website: "your website",
    menu_pdf: "your menu",
    mymanager: "your MyManager shop",
  })[p] ?? p;
